import React, { useState, useEffect } from "react";
import "./App.css";
import Recipes from "./screens/Recipes/Recipes";
import Sidebar from "./components/Sidebar";
import Settings from "./screens/Settings/Settings";
import Dashboard from "./screens/Dashboard/Dashboard";
import DailyMealLogger from "./screens/ProteinTracker/DailyMealLogger";
import LoginScreen from "./screens/Login/LoginScreen";
import useAuth from "./auth/useAuth";
import Support from "./screens/Support/Support";
import DailyMoodLogger from "./screens/MoodLog/DailyMoodLogger";
import DailyMeditationLogger from "./screens/Meditation/DailyMeditationLogger";
import MovementTracker from "./screens/Movement/MovementTracker";
import HydrationTracker from "./screens/Hydration/HydrationTracker";
import SleepTracker from "./screens/Sleep/SleepTracker";  

const App = () => {
  const [streak, setStreak] = useState(0);

  const screens = [
    { label: "Dashboard", href: "dashboard", title: "Dashboard" },
    { label: "Protein Tracker", href: "tracker", title: "Protein Tracker" },
    { label: "Food & Mood", href: "mood", title: "Food & Mood Log" },
    {
      label: "Meditation Journal",
      href: "meditation",
      title: "Meditation Journal",
    },
    {
      label: "Movement Tracker",
      href: "movement",
      title: "Movement Tracker",
    },
    {
      label: "Hydration Tracker",
      href: "hydration",
      title: "Hydration Tracker",
    },
    {
      label: "Sleep Tracker",
      href: "sleep",
      title: "Sleep Tracker",
    },
    { label: "Recipes", href: "recipes", title: "Recipes" },
    { label: "Settings", href: "settings", title: "Settings" },
    { label: "Support", href: "support", title: "Support" },
  ];

  useEffect(() => {
    const root = document.documentElement;
    const storedPreference = localStorage.getItem("theme");
    if (storedPreference === "dark") {
      root.classList.add("dark");
      document.getElementById("hyman-hive-root").classList.add("tw-dark");
    } else {
      root.classList.remove("dark");
      document.getElementById("hyman-hive-root").classList.remove("tw-dark");
    }
  });

  const validScreens = screens
    .filter((s) => !s.locked && s.href !== "#!")
    .map((s) => s.href);
  const initialScreen = window.location.pathname.replace("/", "");
  const [currentScreen, setCurrentScreen] = useState(
    validScreens.includes(initialScreen) ? initialScreen : "dashboard"
  );

  const sidebarItems = screens.map((item) => ({
    ...item,
    active: item.href === currentScreen,
  }));

  const screenTitle =
    screens.find((item) => item.href === currentScreen)?.title || "Dashboard";

  const { isAuthenticated, login, logout, user } = useAuth();

  useEffect(() => {
    // Remove streak on non-logger pages
    if (["dashboard", "recipes", "settings", "support"].includes(currentScreen)) {
      setStreak(0);
    }
  }, [currentScreen]);

  if (!isAuthenticated) {
    return <LoginScreen onSuccess={login} />;
  }

  return (
    <div className="tw-flex tw-flex-col lg:tw-flex-row tw-bg-gray-100 dark:tw-bg-gray-900 dark:tw-text-white tw-min-h-screen">
      <Sidebar
        sidebarItems={sidebarItems}
        onSelectScreen={(href) => {
          setCurrentScreen(href);
          window.history.pushState(null, "", `/${href}`);
        }}
        logout={logout}
      />

      <main className="tw-w-full lg:tw-4/5 tw-flex-1 tw-pt-8 lg:tw-pt-14 tw-pb-4 tw-px-4 lg:tw-px-8 xl:tw-px-12 tw-overflow-y-auto">
        <div className="tw-max-w-[1100px] tw-mx-auto">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-8 lg:tw-mb-12">
            <h1 className="h1 dark:tw-text-white tw-text-2xl lg:tw-text-3xl tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 tw-items-start lg:tw-items-center">
              {screenTitle}
              {streak > 0 && (
                <p className="tw-text-gray-500 tw-bg-green-500 tw-text-sm tw-text-white tw-rounded-full tw-px-4 tw-text-semibold tw-py-2">
                  {streak} day streak 🔥
                </p>
              )}
            </h1>
            <div className="tw-flex tw-items-center tw-gap-3 person">
              <div className="tw-h-10 tw-w-10 tw-rounded-full tw-bg-black tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold">
                {user?.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || "HH"}
              </div>
              <span>
                <span className="tw-hidden lg:tw-inline-block">Welcome</span>
                <span className="lg:tw-hidden">Hi</span>{" "}
                {user?.name ? user.name.split(" ")[0] + "!" : "Member!"}
              </span>
            </div>
          </div>

          <div className="root">
            {currentScreen === "dashboard" && <Dashboard user={user} />}
            {currentScreen === "tracker" && <DailyMealLogger />}
            {currentScreen === "mood" && <DailyMoodLogger streak={streak} setStreak={setStreak} currentScreen={currentScreen} />}
            {currentScreen === "settings" && <Settings />}
            {currentScreen === "recipes" && <Recipes />}
            {currentScreen === "support" && <Support />}
            {currentScreen === "meditation" && (
              <DailyMeditationLogger streak={streak} setStreak={setStreak} currentScreen={currentScreen} />
            )}
            {currentScreen === "movement" && <MovementTracker streak={streak} setStreak={setStreak} currentScreen={currentScreen} />}
            {currentScreen === "hydration" && <HydrationTracker streak={streak} setStreak={setStreak} currentScreen={currentScreen} />}
            {currentScreen === "sleep" && <SleepTracker streak={streak} setStreak={setStreak} currentScreen={currentScreen} />}
            {/* Test */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;
